import {
	debouncedSignal,
	getColorConfig,
	getSpecificColorConfigEntry,
	getWidgetTextColor,
	WeekDays,
} from '@agilox/common';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomNumberPipe } from '@agilox/pipe/custom-number';
import { HeatmapColorConfig, HeatmapCustomTooltipFn, HeatmapData } from '@agilox/ui-common';
import {
	Component,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	signal,
	viewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as d3 from 'd3';
import { TooltipModule } from '../tooltip/tooltip.module';

const CELL_SIZE = 24;
const DAYS_PER_WEEK = 7;
const WEEKDAY_LABEL_WIDTH = 30;
const MONTH_LABEL_HEIGHT = 20;
const GRID_RIGHT_PADDING = 20;
const LABEL_GAP = 8;
const LABEL_FONT_SIZE = '14px';
const LEGEND_TOP_GAP = 20;
const LEGEND_CELL_SIZE = 16;
const LEGEND_TEXT_GAP = 8;
const LEGEND_ENTRY_GAP = 32;
const RENDER_DELAY = 10;
const TOOLTIP_VERTICAL_OFFSET = 4;
const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

@Component({
	selector: 'ui-heatmap',
	templateUrl: './heatmap.component.html',
	imports: [TooltipModule],
})
export class HeatmapComponent implements OnDestroy {
	private translate = inject(TranslateService);
	private customDatePipe = inject(CustomDatePipe);
	private customNumberPipe = inject(CustomNumberPipe);

	customTooltipTextFn = input<HeatmapCustomTooltipFn | undefined>(undefined);
	unit = input<string>('h');
	customColors = input<string[]>([]);
	data = input<HeatmapData[]>([]);

	chart = viewChild<ElementRef<HTMLDivElement> | undefined>('chart');
	weekdayLabels = viewChild<ElementRef<HTMLDivElement> | undefined>('weekdayLabels');
	legend = viewChild<ElementRef<HTMLDivElement> | undefined>('legend');

	toolTipConfig = signal({
		visible: false,
		top: 0,
		left: 0,
		tooltip: '',
	});

	debouncedData = debouncedSignal(this.data, RENDER_DELAY);

	constructor() {
		effect(() => {
			if (this.debouncedData()) {
				this.removeChart();
				if (!this.debouncedData().length) {
					return;
				}
				this.setupChart(this.debouncedData());
			}
		});
	}

	private setupChart(data: HeatmapData[]) {
		const colorConfig = getColorConfig(data, this.customColors());
		const startDate = new Date(data[data.length - 1].date);
		const fullDates = this.generateAllDays(startDate, data, colorConfig);
		this.buildD3Chart(fullDates, colorConfig);
	}

	generateAllDays(startDate: Date, data: HeatmapData[], colorConfig: HeatmapColorConfig[]) {
		const now = new Date(startDate);
		const yearBackFromNow = new Date(startDate);
		yearBackFromNow.setFullYear(now.getFullYear() - 1);

		const fullDates: HeatmapData[] = Array.from({
			length: (now.getTime() - yearBackFromNow.getTime()) / MILLISECONDS_IN_DAY + 1,
		}).map((_, i) => {
			const date = new Date(yearBackFromNow.getTime() + i * MILLISECONDS_IN_DAY)
				.toISOString()
				.slice(0, 10);
			const dataVal = data.find((d) => d.date === date)?.val || 0;
			return {
				date: date,
				val: data.find((d) => d.date === date)?.val || 0,
				color: getSpecificColorConfigEntry(colorConfig, dataVal),
			};
		});

		const firstDateOfArr = new Date(fullDates[0].date);
		while (firstDateOfArr.getDay() !== 1) {
			fullDates.unshift({
				date: new Date(firstDateOfArr.setDate(firstDateOfArr.getDate() - 1))
					.toISOString()
					.slice(0, 10),
				val: undefined,
				color: 'var(--color-secondary-000)',
			});
		}

		return fullDates;
	}

	buildD3Chart(fullData: HeatmapData[], colorConfig: HeatmapColorConfig[]) {
		const chart = this.chart();
		const weekdayLabels = this.weekdayLabels();
		const legend = this.legend();

		if (!chart || !weekdayLabels || !legend) {
			throw new Error('Chart element not found');
		}

		this.removeChart();

		const weekCount = Math.ceil(fullData.length / DAYS_PER_WEEK);
		const gridHeight = DAYS_PER_WEEK * CELL_SIZE;

		const weekdaySvg = d3
			.select(weekdayLabels.nativeElement)
			.append('svg')
			.attr('width', WEEKDAY_LABEL_WIDTH)
			.attr('height', MONTH_LABEL_HEIGHT + gridHeight)
			.style('display', 'block')
			.append('g')
			.attr('transform', `translate(${WEEKDAY_LABEL_WIDTH},${MONTH_LABEL_HEIGHT})`);

		const daysSvg = d3
			.select(chart.nativeElement)
			.append('svg')
			.attr('width', weekCount * CELL_SIZE + GRID_RIGHT_PADDING)
			.attr('height', MONTH_LABEL_HEIGHT + gridHeight)
			.style('display', 'block')
			.append('g')
			.attr('transform', `translate(0,${MONTH_LABEL_HEIGHT})`);

		const legendSvg = d3
			.select(legend.nativeElement)
			.append('svg')
			.attr('height', LEGEND_TOP_GAP + LEGEND_CELL_SIZE)
			.style('display', 'block');

		this.addDaysToChart(daysSvg, fullData);
		this.addWeekDayLabels(weekdaySvg);
		this.addMonthLabels(daysSvg, fullData);
		this.addLegend(legendSvg, colorConfig);
	}

	addDaysToChart(svg: any, fullData: HeatmapData[]) {
		svg
			.selectAll('.day')
			.data(fullData)
			.enter()
			.append('rect')
			.attr('id', (data: HeatmapData) => `day-${data.date}`)
			.attr('class', 'day')
			.attr('width', CELL_SIZE - 1)
			.attr('height', CELL_SIZE - 1)
			.attr('x', (_: HeatmapData, i: number) => Math.floor(i / DAYS_PER_WEEK) * CELL_SIZE)
			.attr('y', (_: HeatmapData, i: number) => (i % DAYS_PER_WEEK) * CELL_SIZE)
			.attr('fill', (d: HeatmapData) => d.color || 'var(--color-secondary-600)')
			.attr('stroke', 'var(--color-secondary-000)')
			.on('touchstart', (event: TouchEvent) => {
				event.preventDefault();
				event.stopPropagation();
			})
			.on('mouseover', (_: MouseEvent, day: HeatmapData) => {
				this.showTooltip(day);
			})
			.on('mouseout', () => this.hideTooltip());
	}

	addWeekDayLabels(svg: any) {
		const daysOfWeek: string[] = Object.values(WeekDays).filter((v) => typeof v !== 'number');

		svg
			.selectAll('.day-label')
			.data(daysOfWeek)
			.enter()
			.append('text')
			.attr('x', -LABEL_GAP)
			.attr('y', (_: any, i: number) => i * CELL_SIZE + CELL_SIZE / 2)
			.attr('dy', '.35em')
			.style('text-anchor', 'end')
			.style('font-size', LABEL_FONT_SIZE)
			.style('fill', getWidgetTextColor())
			.text((d: string, i: number) => (i % 2 === 0 ? this.getWeekdayName(d) : ''));
	}

	addMonthLabels(svg: any, fullData: HeatmapData[]) {
		const millisecondsInWeek = DAYS_PER_WEEK * MILLISECONDS_IN_DAY;

		const firstDate = new Date(fullData[0].date);
		const firstDateMonday = new Date(
			firstDate.getTime() - firstDate.getDay() * MILLISECONDS_IN_DAY
		);
		const lastDate = new Date(fullData[fullData.length - 1].date);

		const months = [];
		let currentMonth = new Date(
			firstDate.getFullYear(),
			firstDate.getMonth() + (firstDateMonday.getDate() === 1 ? 0 : 1),
			1
		);

		while (currentMonth <= lastDate) {
			months.push(new Date(currentMonth));
			currentMonth.setMonth(currentMonth.getMonth() + 1);
		}

		svg
			.selectAll('.month-label')
			.data(months)
			.enter()
			.append('text')
			.attr(
				'x',
				(d: Date) =>
					Math.floor((d.getTime() - firstDateMonday.getTime()) / millisecondsInWeek) * CELL_SIZE
			)
			.attr('y', -LABEL_GAP)
			.style('text-anchor', 'start')
			.style('font-size', LABEL_FONT_SIZE)
			.style('fill', getWidgetTextColor())
			.text((d: Date) => this.getMonthName(d.getMonth()));
	}

	getMonthName(month: number) {
		return this.translate.instant(`datepicker.months_short.${month}`);
	}

	getWeekdayName(weekday: string) {
		return this.translate.instant(`datepicker.weekday.${weekday}`).substring(0, 2).toUpperCase();
	}

	showTooltip(date: HeatmapData): void {
		if (date.val === undefined) {
			return;
		}

		const element = this.chart()?.nativeElement.querySelector(`#day-${date.date}`);
		if (!element) {
			return;
		}

		const rect = element.getBoundingClientRect();
		const left = rect.left + rect.width / 2;
		const top = rect.top;

		let toolTipText = '';

		if (this.customTooltipTextFn()) {
			toolTipText = this.customTooltipTextFn()!(date) || '';
		} else {
			toolTipText =
				this.customDatePipe.transform(date.date) +
				': ' +
				this.customNumberPipe.transform(date.val) +
				'\u00A0' +
				this.unit();
		}

		this.toolTipConfig.set({
			visible: true,
			tooltip: toolTipText,
			left: left,
			top: top + TOOLTIP_VERTICAL_OFFSET,
		});
	}

	hideTooltip() {
		this.toolTipConfig.update((config) => ({
			...config,
			visible: false,
		}));
	}

	addLegend(svg: any, colorConfig: HeatmapColorConfig[]) {
		const boxVerticalCenter = LEGEND_TOP_GAP + LEGEND_CELL_SIZE / 2;
		let xPosition = 0;

		colorConfig.shift();

		d3.timeout(() => {
			colorConfig.forEach((config: HeatmapColorConfig, index: number) => {
				svg
					.append('rect')
					.attr('x', xPosition)
					.attr('y', LEGEND_TOP_GAP)
					.attr('width', LEGEND_CELL_SIZE)
					.attr('height', LEGEND_CELL_SIZE)
					.attr('fill', config.color);

				let text = `${this.translate.instant('heatmap.up_to')} ${config.range.end}\u00A0${this.unit()}`;

				if (index === colorConfig.length - 1) {
					text = `${this.translate.instant('heatmap.over')} ${config.range.start}\u00A0${this.unit()}`;
				}

				const textElement = svg
					.append('text')
					.attr('x', xPosition + LEGEND_CELL_SIZE + LEGEND_TEXT_GAP)
					.attr('y', boxVerticalCenter)
					.style('font-size', LABEL_FONT_SIZE)
					.style('dominant-baseline', 'middle')
					.style('fill', 'var(--color-secondary-800)')
					.text(text);

				const textWidth = textElement.node().getBBox().width;

				xPosition += LEGEND_CELL_SIZE + textWidth + LEGEND_ENTRY_GAP;
			});

			svg.attr('width', xPosition);
		}, RENDER_DELAY);
	}

	private removeChart() {
		[this.chart(), this.weekdayLabels(), this.legend()].forEach((container) => {
			if (!container?.nativeElement) {
				return;
			}

			d3.select(container.nativeElement).select('svg').remove();
		});
	}

	ngOnDestroy() {
		this.removeChart();
	}
}
